export class EnumClass<T extends string | number = string> {
  private static _enumKeys: string[];
  private static _enumValues: EnumClass[];
  private static _externalEnumKeys: Array<string | number>;

  private _enumKey: string;
  private _enumOrdinal: number;

  constructor(private _externalEnumKey?: T) {}

  // When renaming this method, the Enum decorator must be updated as well!!!
  private static closeEnum() {
    delete this._enumKeys;
    delete this._enumValues;
    delete this._externalEnumKeys;

    const enumKeys: string[] = [];
    const enumValues: EnumClass[] = [];
    const externalEnumKeys: Array<string | number> = [];

    for (const [key, value] of Object.entries(this) as [string, EnumClass][]) {
      if (key === 'ctorParameters' || !(value instanceof EnumClass)) {
        continue;
      }

      enumKeys.push(key);

      value._enumKey = key;
      value._enumOrdinal = enumValues.length;
      value._externalEnumKey = value._externalEnumKey ?? key;

      externalEnumKeys.push(value._externalEnumKey);
      enumValues.push(value);
    }

    this._enumKeys = enumKeys;
    this._enumValues = enumValues;
    this._externalEnumKeys = externalEnumKeys;
  }

  public static parse<T extends number | string = string>(str: string): EnumClass<T> | never {
    this.throwErrorIfAccessedThroughBase();
    const index = this._enumKeys.indexOf(str);
    if (index >= 0) {
      // @ts-ignore
      return this._enumValues[index];
    }
    throw Error(`Unable to parse enum value ${str}`);
  }

  public static parseFromExternalKey<T extends number | string = string>(
    externalKey: T
  ): T extends number ? EnumClass<number> : EnumClass<T> | never {
    this.throwErrorIfAccessedThroughBase();
    const index = this._externalEnumKeys.indexOf(externalKey);
    if (index >= 0) {
      // @ts-ignore
      return this._enumValues[index];
    }
    throw Error(`Unable to parse enum value from external key: ${externalKey}`);
  }

  public static [Symbol.iterator]() {
    this.throwErrorIfAccessedThroughBase();
    return this._enumValues[Symbol.iterator]();
  }

  public static values<T extends EnumClass>(): T[];
  public static values(): EnumClass[] {
    this.throwErrorIfAccessedThroughBase();
    return this._enumValues.slice();
  }

  public static keys(): string[] {
    this.throwErrorIfAccessedThroughBase();
    return this._enumKeys.slice();
  }

  private static throwErrorIfAccessedThroughBase(): void | never {
    if (this === EnumClass) {
      throw Error('Base EnumClass accessor methods should not be used');
    }
  }

  public get enumKey(): string {
    return this._enumKey;
  }

  public get enumOrdinal(): number {
    return this._enumOrdinal;
  }

  public get externalEnumKey(): T {
    return this._externalEnumKey;
  }
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function Enum(constructor: any): void {
  const closeEnumMethodName = 'closeEnum';

  if (typeof constructor[closeEnumMethodName] !== 'function') {
    throw Error(`Failed to close enum class ${constructor}`);
  }

  constructor.closeEnum();
}
